import React from 'react'

/**
 * This data extracts the departure summary airline, O&D data
 * and transforms it into an array of recommended filter chips,
 * format identical to Dynamic Filters API response.
 *
 * https://priceline.atlassian.net/browse/AIR-61855
 * As per ticket we are displaying airlines and airport for now,
 * but if required to onboard more, please see
 * ../__stubs__/sampleDynamicFilterResponse
 *
 * on specific chip formats
 *
 * @input departureSummary
 * @returns Array of recommended filter chips data
 */
export const getRecommendedFilters = (departureSummary = {}, stateProps) => {
  const { airline, arrival, departing } = departureSummary

  const { code, image } = airline || {}

  const { airlinesFilter, airportsFilter } = stateProps
  const airports = airportsFilter?.[0]

  const filters = []

  // Airline
  if (code) {
    const isValid = airlinesFilter.find((filter) => filter?.value === code)

    if (isValid) {
      filters.push({
        type: 'AIRLINES',
        filterId: code,
        image: `https://s1.pclncdn.com/design-assets/fly/carrier-logos/${image}?opto&auto=webp&height=32`,
      })
    }
  }

  // Arrival airport
  // 1st slice arrival <-> 2nd slice departure, hence originAirports
  if (arrival?.airport) {
    const { originAirports } = airports
    const isValid = originAirports?.find((filter) => filter?.value === arrival.airport)

    if (isValid) {
      filters.push({
        type: 'AIRPORT',
        filterId: arrival.airport,
      })
    }
  }

  // Departure airport
  // 1st slice departure <-> 2nd slice arrival, hence originAirports
  if (departing?.airport) {
    const { destinationAirports } = airports
    const isValid = destinationAirports?.find((filter) => filter?.value === departing.airport)

    if (isValid) {
      filters.push({
        type: 'AIRPORT',
        filterId: departing.airport,
      })
    }
  }

  return filters
}

/**
 * Decide which airport filter to use when an airport DF chip is selected.
 *
 * @param {Array} originFilter, destinationFilter - current Airport filters applied from state
 * @returns either origin or destination filter array
 */
export const initializeAirportFilters = ({ originFilter = [], destinationFilter = [], filterId }) =>
  originFilter.filters.find((airport) => airport.value === filterId) ? originFilter : destinationFilter

export const getDisplayValue = (props) => {
  const { type, filterId, airlinesFilter = [], originFilter = [] } = props

  let airportsFilter = initializeAirportFilters(props)

  switch (type) {
    case 'AIRPORT':
      let label = airportsFilter === originFilter ? 'Departing from: ' : 'Traveling to: '
      return (
        <>
          {label} <span translate='no'>{filterId}</span>
        </>
      )
    case 'AIRLINES':
      if (Array.isArray(airlinesFilter)) {
        const airline = airlinesFilter?.find((airline) => airline.value === filterId)
        return airline?.primaryLabel
      }
      break
    default:
      return filterId
  }
}
